import * as React from 'react';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import color from 'color';
import ErrorIcon from '@material-ui/icons/Error';
import IconButton from '@material-ui/core/IconButton';
import InfoIcon from '@material-ui/icons/Info';
import Typography from '@material-ui/core/Typography';
import WarningIcon from '@material-ui/icons/Warning';
import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { Paper } from './Paper';
import { useTranslation } from 'react-i18next';

const icons = {
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: ErrorIcon,
  info: InfoIcon
};

const styles = (theme: Theme) =>
  createStyles({
    paper: {
      alignItems: 'center',
      flexDirection: 'row',
      '& a': {
        color: 'inherit'
      },
      '& .primaryText': {
        marginTop: theme.spacing(0.5)
      }
    },
    icon: {
      marginRight: theme.spacing(1)
    },
    dismiss: {
      height: 48
    },
    error: {
      backgroundColor: color(theme.palette.error.main)
        .lighten(1.7)
        .string(),
      '& svg, & .primaryText': {
        color: theme.palette.error.main
      }
    },
    info: {
      backgroundColor: color(theme.palette.primary.light)
        .lighten(1.1)
        .string(),
      '& svg, & .primaryText': {
        color: color(theme.palette.primary.light)
          .darken(0.4)
          .string()
      }
    },
    success: {
      backgroundColor: color(theme.palette.secondary.main)
        .lighten(2.9)
        .string(),
      '& svg, & .primaryText': {
        color: theme.palette.secondary.main
      }
    },
    warning: {
      backgroundColor: color(theme.palette.error.light)
        .lighten(0.5)
        .string(),
      '& svg, & .primaryText': {
        color: color(theme.palette.error.light)
          .darken(0.6)
          .string()
      }
    }
  });

type OwnProps = {
  variant?: 'error' | 'info' | 'success' | 'warning';
  CustomIcon?: JSX.Element;
  primaryText?: string;
  onDismiss?: () => void;
};

type Props = WithStyles<typeof styles> & OwnProps;

const MessageComponent: React.FC<Props> = ({
  primaryText,
  onDismiss,
  classes,
  variant = 'info',
  children,
  CustomIcon
}) => {
  const Icon = icons[variant];
  const { t } = useTranslation();
  return (
    <Paper className={`${classes.paper} ${classes[variant]}`}>
      <div style={{ display: 'flex', flex: 1 }}>
        {CustomIcon ? CustomIcon : <Icon fontSize="large" className={classes.icon} />}
        <div style={primaryText ? undefined : { marginTop: '.375em' }}>
          {primaryText && (
            <Typography className="primaryText" variant="h6">
              {primaryText}
            </Typography>
          )}
          {children}
        </div>
      </div>
      {onDismiss && (
        <IconButton
          className={classes.dismiss}
          aria-label={t('components.message.dismiss', 'Dismiss')}
          onClick={onDismiss}
        >
          <CloseIcon />
        </IconButton>
      )}
    </Paper>
  );
};

export const Message = withStyles(styles)(MessageComponent);
